import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Trash2, GripVertical } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LanguagesTab() {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newLang, setNewLang] = useState({ code: '', name: '', native_name: '' });
  const [saving, setSaving] = useState(false);

  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/languages`);
      setLanguages(res.data);
    } catch (err) {
      toast.error('Failed to load languages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLanguages(); }, []);

  const handleToggle = async (lang) => {
    if (lang.is_default) return;
    try {
      await axios.patch(`${API}/api/languages/${lang.id}/toggle`);
      setLanguages(prev => prev.map(l => 
        l.id === lang.id ? { ...l, is_active: !l.is_active } : l
      ));
      toast.success(`${lang.name} ${lang.is_active ? 'disabled' : 'enabled'}`);
    } catch (err) {
      toast.error('Failed to toggle language');
    }
  };

  const handleAdd = async () => {
    if (!newLang.code || !newLang.name || !newLang.native_name) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/languages`, {
        ...newLang,
        code: newLang.code.toLowerCase(),
        order: languages.length
      });
      setLanguages(prev => [...prev, res.data]);
      setShowAddDialog(false);
      setNewLang({ code: '', name: '', native_name: '' });
      toast.success(`${newLang.name} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add language');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang) => {
    if (lang.is_default) return;
    if (!window.confirm(`Delete ${lang.name}? This will also remove all cached translations for this language.`)) return;
    try {
      await axios.delete(`${API}/api/languages/${lang.id}`);
      setLanguages(prev => prev.filter(l => l.id !== lang.id));
      toast.success(`${lang.name} deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900" data-testid="languages-title">
            Language Management
          </h2>
          <p className="text-sm text-gray-500">
            Manage languages available in the mechanic app. Active languages will appear in the mechanic's language selector.
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-language-btn">
          <Plus className="h-4 w-4 mr-1.5" />
          Add Language
        </Button>
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 w-12">#</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Code</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Language</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Native Name</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {languages.map((lang, idx) => (
              <tr key={lang.id} className="hover:bg-gray-50 transition-colors" data-testid={`language-row-${lang.code}`}>
                <td className="px-4 py-3 text-sm text-gray-400">
                  <GripVertical className="h-4 w-4 inline" /> {idx + 1}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="font-mono text-xs">{lang.code}</Badge>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{lang.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{lang.native_name}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Switch
                      checked={lang.is_active}
                      onCheckedChange={() => handleToggle(lang)}
                      disabled={lang.is_default}
                      data-testid={`toggle-${lang.code}`}
                    />
                    <span className={`text-xs ${lang.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                      {lang.is_default ? 'Default' : lang.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {!lang.is_default && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(lang)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-${lang.code}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>How it works:</strong> Active languages appear in the mechanic app's language selector. 
        When a mechanic selects a language, category names and questions are translated using AI and cached for instant future loads.
        Adding a new language here makes it immediately available in the mechanic app — no app update required.
      </div>

      {/* Add Language Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Language</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Language Code</Label>
              <Input
                value={newLang.code}
                onChange={(e) => setNewLang({ ...newLang, code: e.target.value })}
                placeholder="e.g., fr, de, ja"
                maxLength={5}
                data-testid="new-lang-code"
              />
              <p className="text-xs text-gray-500">ISO 639-1 code (2 letters)</p>
            </div>
            <div className="space-y-2">
              <Label>Language Name (English)</Label>
              <Input
                value={newLang.name}
                onChange={(e) => setNewLang({ ...newLang, name: e.target.value })}
                placeholder="e.g., French, German, Japanese"
                data-testid="new-lang-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Native Name</Label>
              <Input
                value={newLang.native_name}
                onChange={(e) => setNewLang({ ...newLang, native_name: e.target.value })}
                placeholder="e.g., Français, Deutsch, 日本語"
                data-testid="new-lang-native"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="save-language-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Add Language
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
